import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';

type Payload = {
  nationalNumber: string;
};

type Props = ModalProps<Payload>;

const DirectDebitConsent = ({ payload: { nationalNumber } }: Props) => (
  <Modal title="Autogiromedgivande">
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <h4 className="text-body-semibold">Betalningsmottagare</h4>
        <p>Namn: Nordiska Kreditmarknadsaktiebolag</p>
        <p>Organisationsnummer: 556760-6032</p>
        <p>Bankgironummer: 5379-6454</p>
      </div>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Betalare</h4>
        <p>
          Personnummer: <span data-private>{nationalNumber}</span>
        </p>
      </div>

      <h4 className="text-body-semibold">Medgivande till betalning via Autogiro</h4>
      <p>
        Undertecknad (”betalaren”), medger att betalning får göras genom uttag från angivet konto eller av
        betalaren senare angivet konto eller från det konto som är anslutet till angivet bankgironummer, på
        begäran av angiven betalningsmottagare för betalning till denne på viss dag (”förfallodagen”) via
        Autogiro. Betalaren samtycker till att behandling av personuppgifter som lämnats i detta medgivande
        behandlas av betalarens betaltjänstleverantör, betalningsmottagaren, betalningsmottagarens
        betaltjänstleverantör och Bankgirocentralen BGC AB för administration av tjänsten.
        Personuppgiftsansvariga för denna personuppgiftsbehandling är betalarens betaltjänstleverantör,
        betalningsmottagaren samt betalningsmottagarens betaltjänstleverantör. Betalaren kan när som helst
        begära att få tillgång till eller rättelse av personuppgifterna genom att kontakta betalarens
        betaltjänstleverantör. Ytterligare information om behandling av personuppgifter i samband med
        betalningar kan finnas i villkoren för kontot och i avtalet med betalningsmottagaren. Betalaren kan
        när som helst återkalla sitt samtycke, vilket medför att tjänsten i sin helhet avslutas.
      </p>

      <h3 className="text-section-util">Beskrivning</h3>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Allmänt</h4>
        <p>
          Autogiro är en betaltjänst som innebär att betalningar utförs från betalarens konto på initiativ av
          betalningsmottagaren. För att betalaren ska kunna betala via Autogiro, ska betalaren lämna sitt
          medgivande till betalningsmottagaren om att denne får initiera betalningar från betalarens konto.
          Dessutom ska betalarens betaltjänstleverantör (t ex bank eller betalningsinstitut) godkänna att
          kontot kan användas för Autogiro och betalningsmottagaren ska godkänna betalaren som användare av
          Autogiro.
        </p>
      </div>

      <p>
        Betalarens betaltjänstleverantör är inte skyldig att pröva behörigheten av eller meddela betalaren i
        förväg om begärda uttag. Uttag belastas betalarens konto enligt de regler som gäller hos betalarens
        betaltjänstleverantör. Meddelande om uttag får betalaren från sin betaltjänstleverantör. Medgivandet
        kan på betalarens begäran överflyttas till annat konto hos betaltjänstleverantören eller till konto
        hos annan betaltjänstleverantör.
      </p>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Definition av bankdag</h4>
        <p>
          Med bankdag avses alla dagar utom lördag, söndag, midsommarafton, julafton eller nyårsafton eller
          annan allmän helgdag.
        </p>
      </div>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Information om betalning</h4>
        <p>
          Betalaren kommer av betalningsmottagaren att meddelas belopp, förfallodag och betalningssätt senast
          åtta bankdagar före förfallodagen. Detta kan meddelas inför varje enskild förfallodag eller vid ett
          tillfälle avseende flera framtida förfallodagar. Om meddelandet avser flera framtida förfallodagar
          ska meddelandet lämnas senast åtta bankdagar före den första förfallodagen. Detta gäller dock inte
          fall då betalaren godkänt uttaget i samband med köp eller beställning av vara eller tjänst. I sådant
          fall får betalaren meddelande av betalningsmottagaren om belopp, förfallodag och betalningssätt i
          samband med köpet och/eller beställningen. Genom undertecknandet av detta medgivande lämnar
          betalaren sitt samtycke till att betalningar som omfattas av betalningsmottagarens meddelande enligt
          denna punkt genomförs.
        </p>
      </div>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Täckning måste finnas på kontot</h4>
        <p>
          Betalaren ska se till att täckning finns på kontot senast kl. 00.01 på förfallodagen. Har betalaren
          inte täckning på kontot på förfallodagen kan det innebära att betalningar inte blir utförda. Om
          täckning saknas för betalning på förfallodagen får betalningsmottagaren göra ytterligare
          uttagsförsök under de kommande bankdagarna. Betalaren kan på begäran få information från
          betalningsmottagaren om antalet uttagsförsök.
        </p>
      </div>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Stoppa betalning (återkallelse av betalningsorder)</h4>
        <p>
          Betalaren får stoppa en betalning genom att kontakta antingen betalningsmottagaren senast två
          bankdagar före förfallodagen eller sin betaltjänstleverantör senast bankdagen före förfallodagen vid
          den tidpunkt som anges av betaltjänstleverantören.
        </p>
        <p>
          Om betalaren stoppar en betalning enligt ovan innebär det att den aktuella betalningen stoppas vid
          ett enskilt tillfälle. Om betalaren vill att samtliga framtida betalningar som initieras av
          betalningsmottagaren ska stoppas måste betalaren återkalla medgivandet.
        </p>
      </div>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">Medgivandets giltighetstid, återkallelse</h4>
        <p>
          Medgivandet gäller tills vidare. Betalaren har rätt att när som helst återkalla medgivandet genom
          att kontakta betalningsmottagaren eller sin betaltjänstleverantör. Meddelandet om återkallelse av
          medgivandet ska för att stoppa ännu inte genomförda betalningar vara betalningsmottagaren tillhanda
          senast fem bankdagar före förfallodagen alternativt vara betalarens betaltjänstleverantör tillhanda
          senast bankdagen före förfallodagen vid den tidpunkt som anges av betaltjänstleverantören.
        </p>
      </div>

      <div className="flex flex-col">
        <h4 className="text-body-semibold">
          Rätten för betalningsmottagaren och betalarens betaltjänstleverantör att avsluta anslutningen till
          Autogiro
        </h4>
        <p>
          Betalningsmottagaren har rätt att avsluta betalarens anslutning till Autogiro trettio dagar efter
          det att betalningsmottagaren underrättat betalaren härom. Betalningsmottagaren har dock rätt att
          omedelbart avsluta betalarens anslutning till Autogiro om betalaren vid upprepade tillfällen inte
          har haft tillräcklig kontobehållning på förfallodagen eller om det konto som medgivandet avser
          avslutas eller om betalningsmottagaren bedömer att betalaren av annan anledning inte bör delta i
          Autogiro.
        </p>
        <p>
          Betalarens betaltjänstleverantör har rätt att avsluta betalarens anslutning till Autogiro i enlighet
          med de villkor som gäller mellan betalarens betaltjänstleverantör och betalaren.
        </p>
      </div>
    </div>
  </Modal>
);

export default DirectDebitConsent;
